<template>
    <div class="iframe-container"
        v-loading="isLoading"
        element-loading-text="内容加载中..."
        :style="containerStyle">
        <!-- 暂无数据状态 -->
        <div v-if="!src" class="empty-state">
            <div class="empty-icon">📄</div>
            <div class="empty-message">{{ emptyText }}</div>
        </div>

        <!-- 增强错误状态 -->
        <div v-else-if="showError" class="error-state">
            <div class="error-icon">⚠️</div>
            <div class="error-message">
                {{ errorMessage }}
                <a
                    v-if="blockedByPolicy"
                    :href="src"
                    target="_blank"
                    class="retry-link">
                    （新窗口打开）
                </a>
            </div>
        </div>

        <iframe
            v-show="!isLoading && !showError && src"
            :key="iframeKey"
            ref="iframeRef"
            :src="processedSrc"
            :title="title"
            :sandbox="sandbox"
            @load="handleLoad"
            @error="handleError"
            class="iframe-content"
            frameborder="0"
            allowfullscreen></iframe>
    </div>
  </template>

<script>
export default {
    name: 'IframeContainer',
    props: {
        loadingText: { type: String, default: '内容加载中...' },
        timeout: { type: Number, default: 10000 },
        src: { type: String, required: true, default: '' },
        width: { type: [String, Number], default: '100%' },
        height: { type: [String, Number], default: '100%' },
        title: { type: String, default: '嵌入式内容' },
        sandbox: { type: String, default: 'allow-scripts allow-same-origin' },
        emptyText: { type: String, default: '暂无数据' }
    },
    data() {
        return {
            iframeKey: 0,
            isLoading: true,
            isError: false,
            blockedByPolicy: false,
            timeoutId: null,
            iframeRef: null
        };
    },
    computed: {
        processedSrc() {
            return this.src; // 代理逻辑可在此处添加
        },
        showError() {
            return this.isError || this.blockedByPolicy;
        },
        errorMessage() {
            return this.blockedByPolicy ?
                '内容安全策略阻止加载' :
                '内容加载失败，请检查链接有效性';
        },
        containerStyle() {
            return {
                width: typeof this.width === 'number' ? `${this.width}px` : this.width,
                height: typeof this.height === 'number' ? `${this.height}px` : this.height,
                position: 'relative'
            };
        }
    },
    methods: {
        startTimer() {
            this.timeoutId = setTimeout(() => {
                if (this.isLoading) {
                    this.isError = true;
                    this.isLoading = false;
                }
            }, this.timeout);
        },
        handleLoad() {
            clearTimeout(this.timeoutId);
            this.isLoading = false;
            this.isError = false;
            this.blockedByPolicy = false;
        },
        handleError(e) {
            clearTimeout(this.timeoutId);
            this.isLoading = false;
            this.isError = true;

            const target = e.target || {};
            const contentWindow = ('contentWindow' in target) ? target.contentWindow : null;
            const errorMsg = (contentWindow && contentWindow.location.href) || '';
            this.blockedByPolicy = /denied|blocked|X-Frame-Options/i.test(errorMsg);
        },
        refresh() {
            this.isLoading = true;
            this.isError = false;
            this.blockedByPolicy = false;
            this.iframeKey++;
            this.startTimer();
        }
    },
    mounted() {
        this.startTimer();
    },
    beforeDestroy() {
        clearTimeout(this.timeoutId);
    }
};
</script>

  <style lang="less" scoped>
  .iframe-container {
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      overflow: hidden;
      background-color: #f5f7fa;

      position: relative;

      .empty-state {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 2;

          .empty-icon {
              font-size: 40px;
              margin-bottom: 15px;
              color: #909399;
          }

          .empty-message {
              color: #909399;
              font-size: 14px;
          }
      }

      .error-state {
          /* 增强错误提示样式 */
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 2;

          .error-icon {
              font-size: 40px;
              margin-bottom: 15px;
          }

          .error-message {
              color: #f56c6c;
              text-align: center;
          }

          .retry-link {
              color: #409eff;
              text-decoration: underline;
              margin-left: 8px;
          }
      }

      .iframe-content {
          width: 100%;
          height: 100%;
          display: block;
      }

      .loading-state,
      .error-state {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #909399;
          font-size: 14px;
      }

      .error-state {
          color: #f56c6c;
      }
  }
  </style>